/**
 * CBO/Organization Form Component
 * Modal form for creating or editing organizations
 */

import React, { useState, useEffect } from 'react';
import { X, Building, Mail, Phone, MapPin, Globe, Upload, ChevronDown, ChevronUp, CreditCard, FileText } from 'lucide-react';
import { organizationsAPI, type Organization, type OrganizationCreate } from '@/services/api/organizations';
import { CommunicationProviderSettings } from '../settings/CommunicationProviderSettings';
import { config as productConfig } from '@/src/config';
import PhoneInput from '@/components/ui/PhoneInput';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface CBOFormProps {
  organization?: Organization | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CBOForm({ organization, isOpen, onClose, onSuccess }: CBOFormProps) {
  const { t } = useAppTranslation();
  const [formData, setFormData] = useState<OrganizationCreate>({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
    logo_url: '',
    status: 'active',
    settings: {},
    customer_status: 'DEMO',
    // Billing fields
    billing_model: 'trust_npi',
    npi: '',
    ein: '',
    taxonomy_code: '',
    is_billing_provider: false,
    billing_name: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_phone: '',
    billing_contact_name: '',
    billing_contact_email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCommunicationSettings, setShowCommunicationSettings] = useState(false);
  const [showBillingSettings, setShowBillingSettings] = useState(false);

  const isEditing = !!organization;

  useEffect(() => {
    if (organization) {
      console.log('🔄 Loading organization into form:', organization);
      setFormData({
        name: organization.name,
        description: organization.description || '',
        contact_email: organization.contact_email || '',
        contact_phone: organization.contact_phone || '',
        address: organization.address || '',
        website: organization.website || '',
        logo_url: organization.logo_url || '',
        status: organization.status || 'active',
        settings: organization.settings || {},
        customer_status: organization.customer_status || 'DEMO',
        // Billing fields
        billing_model: organization.billing_model || 'trust_npi',
        npi: organization.npi || '',
        ein: organization.ein || '',
        taxonomy_code: organization.taxonomy_code || '',
        is_billing_provider: organization.is_billing_provider || false,
        billing_name: organization.billing_name || '',
        billing_address_line1: organization.billing_address_line1 || '',
        billing_address_line2: organization.billing_address_line2 || '',
        billing_city: organization.billing_city || '',
        billing_state: organization.billing_state || '',
        billing_zip: organization.billing_zip || '',
        billing_phone: organization.billing_phone || '',
        billing_contact_name: organization.billing_contact_name || '',
        billing_contact_email: organization.billing_contact_email || '',
      });
      console.log('✅ Form data set with status:', organization.status || 'active', 'customer_status:', organization.customer_status || 'DEMO');
    } else {
      // Reset form for new organization
      setFormData({
        name: '',
        description: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        website: '',
        logo_url: '',
        status: 'active',
        settings: {},
        customer_status: 'DEMO',
        // Billing fields
        billing_model: 'trust_npi',
        npi: '',
        ein: '',
        taxonomy_code: '',
        is_billing_provider: false,
        billing_name: '',
        billing_address_line1: '',
        billing_address_line2: '',
        billing_city: '',
        billing_state: '',
        billing_zip: '',
        billing_phone: '',
        billing_contact_name: '',
        billing_contact_email: '',
      });
    }
    setError(null);
  }, [organization, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    console.log('🔄 CBOForm handleSubmit called', { isEditing, organization, formData });

    try {
      if (isEditing && organization) {
        console.log('🔄 Updating organization:', organization.id, formData);
        const result = await organizationsAPI.updateOrganization(organization.id, formData);
        console.log('✅ Organization updated successfully:', result);
      } else {
        console.log('🔄 Creating new organization:', formData);
        const result = await organizationsAPI.createOrganization(formData);
        console.log('✅ Organization created successfully:', result);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('❌ Failed to save organization:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save organization');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof OrganizationCreate, value: any) => {
    console.log(`🔄 Form field changed: ${field} = ${value}`);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {isEditing ? 'Edit Organization' : 'Create New Organization'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Organization Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.organization_name', 'Organization Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.abc_community_services', 'ABC Community Services')}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.description', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.brief_description_of_the_organization', 'Brief description of the organization...')}
                />
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {t('admin.contact_email', 'Contact Email')}
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@organization.org"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t('admin.contact_phone', 'Contact Phone')}
                  </label>
                  <PhoneInput
                    value={formData.contact_phone}
                    onChange={(v) => handleChange('contact_phone', v)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  {t('admin.address', 'Address')}
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  {t('admin.website', 'Website')}
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://www.organization.org"
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {t('admin.logo_url', 'Logo URL')}
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/logo.png"
                />
                {formData.logo_url && (
                  <div className="mt-2">
                    <img
                      src={formData.logo_url}
                      alt={t('admin.logo_preview', 'Logo preview')}
                      className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.status', 'Status')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value as 'active' | 'inactive')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">{t('admin.active', 'Active')}</option>
                    <option value="inactive">{t('admin.inactive', 'Inactive')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.customer_status', 'Customer Status')}
                  </label>
                  <select
                    value={formData.customer_status}
                    onChange={(e) => handleChange('customer_status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="DEMO">{t('admin.demo', 'Demo')}</option>
                    <option value="TRIAL">{t('admin.trial', 'Trial')}</option>
                    <option value="ACTIVE">{t('admin.active', 'Active')}</option>
                    <option value="INACTIVE">{t('admin.inactive_1', 'Inactive')}</option>
                  </select>
                </div>
              </div>

              {/* Billing Configuration — healthcare-only. This entire section
                  configures direct-to-payer billing via NPI/EIN/taxonomy_code
                  + Medi-Cal "Trust NPI vs Organization NPI" billing model.
                  Triveny.ai is a B2B marketing/sales product and bills
                  customers via Stripe Connect + invoice_management — the
                  payer-facing claims model doesn't apply, so hide the section
                  entirely. Triveny tenants manage billing in RevOps /
                  platform-invoices instead. */}
              {!productConfig.isTriveny && (
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowBillingSettings(!showBillingSettings)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      <CreditCard className="w-4 h-4 inline mr-2" />
                      {t('admin.billing_configuration', 'Billing Configuration')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.configure_billing_model_npi_and', 'Configure billing model, NPI, and provider information')}</p>
                  </div>
                  {showBillingSettings ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {showBillingSettings && (
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    {/* Billing Model Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('admin.billing_model', 'Billing Model')} <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="billing_model"
                            value="trust_npi"
                            checked={formData.billing_model === 'trust_npi'}
                            onChange={(e) => handleChange('billing_model', e.target.value)}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{t('admin.trust_platform_billing', 'Trust Platform Billing')}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {t('admin.trust_marketplace_manages_billing_and', 'Trust Marketplace manages billing and claims submission on your behalf using our NPI')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="billing_model"
                            value="organization_npi"
                            checked={formData.billing_model === 'organization_npi'}
                            onChange={(e) => {
                              handleChange('billing_model', e.target.value);
                              handleChange('is_billing_provider', true);
                            }}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{t('admin.organization_billing', 'Organization Billing')}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {t('admin.your_organization_bills_payers_directly', 'Your organization bills payers directly using your own NPI and credentials')}
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Organization NPI Fields - Only show when organization_npi selected */}
                    {formData.billing_model === 'organization_npi' && (
                      <>
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('admin.provider_identification', 'Provider Identification')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                <FileText className="w-4 h-4 inline mr-1" />
                                NPI (National Provider Identifier) <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.npi}
                                onChange={(e) => handleChange('npi', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="10-digit NPI"
                                maxLength={10}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.ein_tax_id', 'EIN (Tax ID)')} <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.ein}
                                onChange={(e) => handleChange('ein', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="XX-XXXXXXX"
                                maxLength={10}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.taxonomy_code', 'Taxonomy Code')}
                              </label>
                              <input
                                type="text"
                                value={formData.taxonomy_code}
                                onChange={(e) => handleChange('taxonomy_code', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., 251S00000X"
                                maxLength={10}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.legal_billing_name', 'Legal Billing Name')}
                              </label>
                              <input
                                type="text"
                                value={formData.billing_name}
                                onChange={(e) => handleChange('billing_name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Legal name for billing (if different)"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Billing Address */}
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('admin.billing_address', 'Billing Address')}</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.address_line_1', 'Address Line 1')}
                              </label>
                              <input
                                type="text"
                                value={formData.billing_address_line1}
                                onChange={(e) => handleChange('billing_address_line1', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t('admin.street_address', 'Street address')}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.address_line_2', 'Address Line 2')}
                              </label>
                              <input
                                type="text"
                                value={formData.billing_address_line2}
                                onChange={(e) => handleChange('billing_address_line2', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t('admin.suite_unit_building_floor_etc', 'Suite, unit, building, floor, etc.')}
                              />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('admin.city', 'City')}
                                </label>
                                <input
                                  type="text"
                                  value={formData.billing_city}
                                  onChange={(e) => handleChange('billing_city', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('admin.state', 'State')}
                                </label>
                                <input
                                  type="text"
                                  value={formData.billing_state}
                                  onChange={(e) => handleChange('billing_state', e.target.value.toUpperCase())}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="CA"
                                  maxLength={2}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  ZIP
                                </label>
                                <input
                                  type="text"
                                  value={formData.billing_zip}
                                  onChange={(e) => handleChange('billing_zip', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="12345"
                                  maxLength={10}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Billing Contact */}
                        <div className="border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{t('admin.billing_contact', 'Billing Contact')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.contact_name', 'Contact Name')}
                              </label>
                              <input
                                type="text"
                                value={formData.billing_contact_name}
                                onChange={(e) => handleChange('billing_contact_name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t('admin.billing_department_contact', 'Billing department contact')}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('admin.phone', 'Phone')}
                              </label>
                              <PhoneInput
                                value={formData.billing_phone}
                                onChange={(v) => handleChange('billing_phone', v)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="(555) 123-4567"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('admin.billing_email', 'Billing Email')}
                            </label>
                            <input
                              type="email"
                              value={formData.billing_contact_email}
                              onChange={(e) => handleChange('billing_contact_email', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="billing@organization.org"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Trust NPI Info */}
                    {formData.billing_model === 'trust_npi' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900">{t('admin.trust_platform_billing_1', 'Trust Platform Billing')}</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Claims will be submitted under Trust Marketplace's NPI. You'll have access to
                              billing reports and reimbursement tracking through the Billing page.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Communication Settings */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowCommunicationSettings(!showCommunicationSettings)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                >
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{t('admin.communication_settings', 'Communication Settings')}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.configure_byop_qr_codes_and', 'Configure BYOP, QR codes, and user phone numbers')}</p>
                  </div>
                  {showCommunicationSettings ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {showCommunicationSettings && (
                  <div className="border-t border-gray-200 p-4">
                    <CommunicationProviderSettings
                      organizationId={organization?.id || 0}
                      initialSettings={formData.settings?.communication}
                      onSettingsChange={(commSettings) => {
                        handleChange('settings', {
                          ...formData.settings,
                          communication: commSettings
                        });
                      }}
                      isSetupMode={true}
                      showAdvancedFeatures={false}
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {t('admin.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{t('admin.saving', 'Saving...')}</span>
                    </>
                  ) : (
                    <span>{isEditing ? 'Update Organization' : 'Create Organization'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
